/**
 * Season classification for race results.
 *
 * Coaches/athletes tag each race with a season type (XC / Indoor /
 * Outdoor) when they create it. The improvement tracker groups
 * benchmarks into season columns using that tag — explicit beats
 * inference, since boundary races (late July outdoor, late August
 * indoor, etc.) get miscategorized by the old date-based heuristic.
 *
 * The "year" portion of a season label still derives from the race
 * date: indoor seasons span the academic year, so an October race
 * tagged "indoor" belongs to the following calendar year's indoor
 * season. XC sorts under the next academic year so it precedes that
 * year's indoor.
 */

export const SEASON_OPTIONS = [
  { value: 'xc', label: 'Cross Country' },
  { value: 'indoor', label: 'Indoor Track' },
  { value: 'outdoor', label: 'Outdoor Track' },
];

/**
 * Build a season label + sort key from an explicit tag + date.
 * Returns null if either is missing.
 */
export function seasonLabelFromTag(tag, dateStr) {
  if (!tag || !dateStr) return null;
  const [yearStr, monthStr] = dateStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  if (!year) return null;

  if (tag === 'xc') {
    // Fall season — sort under the academic year that follows so it
    // comes before that year's indoor/outdoor in the table.
    return { label: `'${String(year).slice(2)} XC`, sort: (year + 1) * 3 };
  }
  if (tag === 'indoor') {
    // Academic year span: fall races (Aug–Dec) belong to next
    // calendar year's indoor season; spring races stay on the same
    // year. Matches how schools talk about "'27 Indoors" = winter
    // 2026-27.
    const sy = month >= 8 ? year + 1 : year;
    return { label: `'${String(sy).slice(2)} Indoors`, sort: sy * 3 + 1 };
  }
  if (tag === 'outdoor') {
    return { label: `'${String(year).slice(2)} Outdoors`, sort: year * 3 + 2 };
  }
  return null;
}

/**
 * Date-only fallback used for legacy benchmarks that predate the
 * `season` column. Mirrors the original inline `getSeasonKey`
 * boundaries: Dec 1 – Mar 15 = Indoors, Mar 16 – Jul 31 = Outdoors,
 * Aug 1 – Nov 30 = XC.
 */
export function seasonLabelFromDate(dateStr) {
  if (!dateStr) return null;
  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  if (!year) return null;
  if (month === 12) {
    const sy = year + 1;
    return { label: `'${String(sy).slice(2)} Indoors`, sort: sy * 3 + 1 };
  }
  if (month < 3 || (month === 3 && day <= 15)) {
    return { label: `'${String(year).slice(2)} Indoors`, sort: year * 3 + 1 };
  }
  if (month <= 7) {
    return { label: `'${String(year).slice(2)} Outdoors`, sort: year * 3 + 2 };
  }
  return { label: `'${String(year).slice(2)} XC`, sort: (year + 1) * 3 };
}

/**
 * Single entry point for the improvement aggregator: prefer the
 * explicit tag stored on the benchmark; fall back to date-based
 * inference for older rows.
 */
export function seasonKeyForBenchmark(benchmark) {
  if (benchmark?.season) {
    return seasonLabelFromTag(benchmark.season, benchmark.race_date);
  }
  return seasonLabelFromDate(benchmark?.race_date);
}
